import { Box } from '@mui/material';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FocusEvent,
  type PointerEvent,
} from 'react';

import {
  useBoardDrawingStore,
  useWallParticipantsStore,
  useWorkspaceUiStore,
  type WallBoard,
} from '@walls/store';
import { useShapeCommands } from '../shapes/hooks/useShapeCommands';

interface TextInputOverlayProps {
  board: WallBoard;
}

export function TextInputOverlay({ board }: TextInputOverlayProps) {
  const textInputDraft = useBoardDrawingStore((state) => state.textInputDraft);
  const setTextInputDraft = useBoardDrawingStore((state) => state.setTextInputDraft);
  const currentParticipantId = useWallParticipantsStore((state) => state.currentParticipantId);
  const selectedFontColor = useWorkspaceUiStore((state) => state.selectedFontColor);
  const selectedFontSize = useWorkspaceUiStore((state) => state.selectedFontSize);
  const selectedFontStyle = useWorkspaceUiStore((state) => state.selectedFontStyle);
  const setSelectedTool = useWorkspaceUiStore((state) => state.setSelectedTool);
  const zoom = useWorkspaceUiStore((state) => state.zoom);
  const { createShape } = useShapeCommands(board);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLSpanElement>(null);
  const savedRef = useRef(false);
  const dragRef = useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    startX: number;
    startY: number;
  } | null>(null);
  const [position, setPosition] = useState(() => ({
    x: textInputDraft?.x ?? 0,
    y: textInputDraft?.y ?? 0,
  }));

  useEffect(() => {
    if (textInputDraft) {
      savedRef.current = false;
      setPosition({ x: textInputDraft.x, y: textInputDraft.y });
      inputRef.current?.focus();
    }
  }, [textInputDraft]);

  const saveText = useCallback(() => {
    if (savedRef.current) {
      return;
    }

    savedRef.current = true;
    const text = inputRef.current?.innerText.trim();

    if (text) {
      void createShape({
        id: `local-${board.id}-text-${Date.now()}`,
        board: board.id,
        ownerId: currentParticipantId ?? undefined,
        type: 'text',
        isLocked: false,
        zPos: 999,
        attributes: {
          color: selectedFontColor,
          fontFamily: selectedFontStyle,
          fontSize: selectedFontSize,
          text,
          x: position.x + 6,
          y: position.y + 8,
        },
      });
    }

    setTextInputDraft(null);
    setSelectedTool('pointer');
  }, [
    board.id,
    createShape,
    currentParticipantId,
    position.x,
    position.y,
    selectedFontColor,
    selectedFontSize,
    selectedFontStyle,
    setSelectedTool,
    setTextInputDraft,
  ]);

  if (!textInputDraft) {
    return null;
  }

  const handleBlur = (event: FocusEvent<HTMLSpanElement>) => {
    if (containerRef.current?.contains(event.relatedTarget as Node | null)) {
      return;
    }

    saveText();
  };

  const handleDragStart = (event: PointerEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: position.x,
      startY: position.y,
    };
  };

  const handleDragMove = (event: PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;

    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    setPosition({
      x: drag.startX + (event.clientX - drag.startClientX) / zoom,
      y: drag.startY + (event.clientY - drag.startClientY) / zoom,
    });
  };

  const handleDragEnd = (event: PointerEvent<HTMLElement>) => {
    if (dragRef.current?.pointerId !== event.pointerId) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    dragRef.current = null;
    inputRef.current?.focus();
  };

  return (
    <>
      <Box
        aria-hidden="true"
        onPointerDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          saveText();
        }}
        sx={{
          inset: 0,
          pointerEvents: 'auto',
          position: 'absolute',
          zIndex: 900,
        }}
      />
      <Box
        ref={containerRef}
        sx={{
          border: '2px solid #7dc9fb',
          display: 'inline-flex',
          left: position.x,
          p: '4px',
          pointerEvents: 'auto',
          position: 'absolute',
          top: position.y,
          zIndex: 901,
        }}
      >
        <Box
          component="img"
          alt=""
          src="/app/_collage/img/handle.png"
          onPointerDown={handleDragStart}
          onPointerMove={handleDragMove}
          onPointerUp={handleDragEnd}
          onPointerCancel={handleDragEnd}
          sx={{
            cursor: 'move',
            height: 13,
            left: -7,
            position: 'absolute',
            top: -7,
            width: 13,
          }}
        />
        <Box
          component="span"
          id="text-input"
          ref={inputRef}
          contentEditable
          suppressContentEditableWarning
          onBlur={handleBlur}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              saveText();
            }
          }}
          sx={{
            bgcolor: 'rgba(0,0,0,0)',
            color: selectedFontColor,
            display: 'inline-block',
            fontFamily: selectedFontStyle,
            fontSize: selectedFontSize,
            minHeight: 34,
            minWidth: 160,
            outline: 0,
            whiteSpace: 'pre',
          }}
        >
          {textInputDraft.text}
        </Box>
      </Box>
    </>
  );
}
